import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash';

import type { SupportedLanguage } from '../../../translate';
import { text } from './epiHelpers';
import { visibleIdmp } from './schemaUtils';
import type { EpiComposition, EpiIdmpLink } from '../types/types';

/**
 * Composition-level IDMP operations.
 *
 * Every IDMP on the ePI belongs to exactly one composition per document type —
 * the compositions partition the set. That invariant is why there is no plain
 * add or delete here: a new composition is made by *peeling* IDMPs off an
 * existing one, and a composition is removed by *merging* it into a sibling so
 * its IDMPs stay covered.
 *
 * `composition.medicinalProducts` holds internal references into the ePI's own
 * `medicinalProducts[]`, so an entry's `id` is the IDMP's own uuid and is
 * copied rather than minted. Sections carry no IDMP, so none of this touches
 * section data beyond copying it wholesale.
 */

/**
 * Whether a title is already taken within a document type.
 *
 * Only *live* siblings count. A removed document keeps its title in FE state
 * until the next save, and counting it would reserve a title that is no longer
 * on screen. The composition being renamed doesn't collide with itself either.
 */
export function isTitleTaken(
  compositions: EpiComposition[],
  selfId: string | undefined,
  title: string,
  lang: SupportedLanguage
): boolean {
  return compositions.some(
    c => c.id !== selfId && !c.toBeDeleted && text(c.title, lang) === title
  );
}

/**
 * Copies a link, dropping everything else. The server returns these bare, but
 * `enrichSparseIdRefs` hydrates any `{id}`-only object from elsewhere in the
 * instance before the editor ever sees it, so a composition entry read back
 * carries the root reference's fields — which the schema no longer allows.
 */
const keepLink = (r: EpiIdmpLink): EpiIdmpLink => ({ id: r.id });

/** The live (not soft-deleted) links, by IDMP id. */
const liveIds = (idmp: EpiIdmpLink[]): Set<string> =>
  new Set(idmp.filter(r => !r.toBeDeleted).map(r => r.id));

/** Soft-removes the given IDMPs from a composition. */
export function peelIdmpsFromComposition(
  source: EpiComposition,
  peeledIdmpIds: string[]
): EpiComposition {
  const peeled = new Set(peeledIdmpIds);
  return {
    ...source,
    medicinalProducts: source.medicinalProducts.map(r =>
      peeled.has(r.id) && !r.toBeDeleted ? { ...r, toBeDeleted: true } : r
    ),
  };
}

/**
 * Drops one medicinal product from a composition.
 *
 * Returns the updated composition plus whether that emptied it. A composition
 * covering nothing is not valid — the schema puts `minItems: 1` on
 * `composition.medicinalProducts` — and a document covering no product has
 * nothing to say, so the caller soft-deletes it, losing its sections. Both
 * halves are soft deletes: the BE merges arrays by `id` and only removes what
 * it is told to.
 */
export function removeIdmpFromComposition(
  composition: EpiComposition,
  idmpId: string
): { composition: EpiComposition; emptied: boolean } | null {
  const covers = composition.medicinalProducts.some(
    r => r.id === idmpId && !r.toBeDeleted
  );
  if (!covers) return null;

  const next = peelIdmpsFromComposition(composition, [idmpId]);
  const emptied = visibleIdmp(next.medicinalProducts).length === 0;
  return {
    composition: emptied ? { ...next, toBeDeleted: true } : next,
    emptied,
  };
}

/** The composition's live links for the given IDMPs, ready to reuse. */
export function pickIdmpRefs(
  source: EpiComposition,
  idmpIds: string[]
): EpiIdmpLink[] {
  const wanted = new Set(idmpIds);
  return source.medicinalProducts
    .filter(r => !r.toBeDeleted && wanted.has(r.id))
    .map(keepLink);
}

/**
 * A new composition holding a copy of the source's sections, scoped to the
 * peeled IDMPs. The source is left alone — pair this with
 * {@link peelIdmpsFromComposition}.
 *
 * Section ids are copied as-is: they only have to be unique within their own
 * array, and the BE regenerates every nested id when it creates the new
 * composition.
 */
export function cloneCompositionForIdmps(
  source: EpiComposition,
  peeledIdmpIds: string[],
  order: number
): EpiComposition {
  return {
    id: uuid(),
    title: source.title,
    rootTitle: source.rootTitle,
    rootContent: source.rootContent,
    medicinalProducts: pickIdmpRefs(source, peeledIdmpIds),
    rmsCode: source.rmsCode,
    level: source.level,
    sequenceOrder: source.sequenceOrder,
    order,
    sections: cloneDeep(source.sections),
  };
}

/** Adds links a composition doesn't already carry. */
export function addIdmpsToComposition(
  composition: EpiComposition,
  refs: EpiIdmpLink[]
): EpiComposition {
  const have = liveIds(composition.medicinalProducts);
  const fresh = refs.filter(r => !have.has(r.id)).map(keepLink);
  return fresh.length === 0
    ? composition
    : {
        ...composition,
        medicinalProducts: [...composition.medicinalProducts, ...fresh],
      };
}

/**
 * Transfers the source's IDMPs to the target and soft-deletes the source.
 *
 * The source's sections, title and edits are **lost** — the caller confirms
 * that with the user first. Returns null when the inputs aren't valid
 * candidates (same composition, either already deleted, or nothing to
 * transfer).
 */
export function mergeCompositionIntoSibling(
  source: EpiComposition,
  target: EpiComposition
): { updatedTarget: EpiComposition; softDeletedSource: EpiComposition } | null {
  if (source.id === target.id) return null;
  if (source.toBeDeleted || target.toBeDeleted) return null;

  const transferred = source.medicinalProducts.filter(r => !r.toBeDeleted);
  if (transferred.length === 0) return null;

  return {
    updatedTarget: addIdmpsToComposition(target, transferred),
    softDeletedSource: { ...source, toBeDeleted: true },
  };
}
